import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTimezone, sleep } from '@mgm/common/utils';
import moment, { Moment } from 'moment-timezone';
import { TBaseTable } from '@mgm/common/components/table';
import { useFilter } from '../../../../hooks/useFilter';
import { CustomToolbar } from '../../../../components/table';
import { useSheetsFilters } from '../../hooks';
import { useGetSheetsAPI } from '../../../../hooks/api/useSheets';
import { useRangePicker } from '../../../../hooks/useCommon';

const TimesheetsTableHeader: FC<TBaseTable> = ({
  apiRef,
  printRef,
  handleScrollTable,
  isFullTable = false,
  setIsFullTable = () => {},
  handleExport,
}) => {
  const { timesheetsFilterItems } = useSheetsFilters();
  const { filterItems } = useFilter(timesheetsFilterItems);

  const { t } = useTranslation('sheets');

  const [isOpenCustomPicker, setIsOpenCustomPicker] = useState(false);

  const [menuOption, setMenuOption] = useState(t('thisMonth', { ns: 'dashboard' }));
  const [menuValue, setMenuValue] = useState('thisMonth');

  const {
    chosenDates, isBtnDisabled, handleSetDates,
  } = useRangePicker();

  const { isFetching, refetch } = useGetSheetsAPI({
    isMainSheetsPage: false,
    dates: chosenDates,
  });

  const getSheetsByDates = async () => {
    await sleep(1000);
    await refetch();
  };

  const items = useMemo(() => {
    const timezone = getTimezone();

    const dateRangePresets = {
      yesterday: [
        moment.tz(timezone).subtract(1, 'day').startOf('day'),
        moment.tz(timezone).subtract(1, 'day').endOf('day'),
      ],
      today: [
        moment.tz(timezone).startOf('day'),
        moment.tz(timezone).endOf('day'),
      ],
      last7Days: [
        moment.tz(timezone).subtract(6, 'days').startOf('day'),
        moment.tz(timezone).endOf('day'),
      ],
      last14Days: [
        moment.tz(timezone).subtract(13, 'days').startOf('day'),
        moment.tz(timezone).endOf('day'),
      ],
      last30Days: [
        moment.tz(timezone).subtract(29, 'days').startOf('day'),
        moment.tz(timezone).endOf('day'),
      ],
      thisMonth: [
        moment.tz(timezone).startOf('month'),
        moment.tz(timezone).endOf('month'),
      ],
    };

    const createItem = (name: string, value: string) => ({
      name,
      value,
      isSelected: menuValue === value,
      action: async () => {
        setMenuOption(name);
        setMenuValue(value);

        if (value !== 'custom') {
          handleSetDates(dateRangePresets[value as keyof typeof dateRangePresets] as [Moment, Moment]);
          await getSheetsByDates();
        }
      },
    });

    return [
      createItem(t('yesterday', { ns: 'dashboard' }), 'yesterday'),
      createItem(t('today', { ns: 'schedule' }), 'today'),
      createItem(t('lastSevenDays', { ns: 'dashboard' }), 'last7Days'),
      createItem(t('last14Days', { ns: 'dashboard' }), 'last14Days'),
      createItem(t('last30Days', { ns: 'dashboard' }), 'last30Days'),
      createItem(t('thisMonth', { ns: 'dashboard' }), 'thisMonth'),
      {
        name: t('customFixedDateRange', { ns: 'dashboard' }),
        value: 'custom',
        isSelected: menuValue === 'custom',
        action: () => {
          setMenuValue('custom');
          setIsOpenCustomPicker(true);
        },
      },
    ];
  }, [menuValue]);

  return (
    <CustomToolbar
      handleScrollTable={handleScrollTable}
      isDisplayPicker
      printRef={printRef}
      filterItems={filterItems}
      apiRef={apiRef}
      pickerButtonText={t('load', { ns: 'common' })}
      onPickerSubmit={getSheetsByDates}
      isPickerBtnDisable={isBtnDisabled}
      isPickerLoading={isFetching}
      chosenDates={chosenDates}
      handleSetDates={handleSetDates}
      isFullTable={isFullTable}
      setFullTable={setIsFullTable}
      isSheets
      handleExport={handleExport}
      isOpenCustomPicker={isOpenCustomPicker}
      selectedPickerName={menuOption}
      setIsOpenCustomPicker={setIsOpenCustomPicker}
      pickerItems={items}
      isDisplaySelectedMembers
    />
  );
};

export {
  TimesheetsTableHeader,
};
